import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FeedbackCategory } from 'nekst-api';
import {
  FormTemplate,
  TextArea,
  SubmitButton,
  CancelButton,
} from 'ui-builder';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import InChatFormTheme from './InChatFormTheme';
import FeedbackCategoryDropdown from './FeedbackCategoryDropdown';
import styles from '../ChatWidget.module.scss';

type WidgetState = 'idle' | 'form' | 'success';

interface FeedbackFormData {
  description: string;
  category: FeedbackCategory | '';
}

function SuccessMessage(props: { onDismiss: () => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      props.onDismiss();
    }, 5000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [props.onDismiss]);

  return (
    <button
      type="button"
      className={styles.reportIssueSuccess}
      onClick={props.onDismiss}
      data-testid="chat-report-issue-success"
    >
      Thanks for helping us improve Nekst AI!
    </button>
  );
}

function ReportIssueForm(props: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { submitFeedback } = useChatWidgetContext();

  return (
    <InChatFormTheme>
      <FormTemplate<FeedbackFormData>
        submitFormFunc={async (data) => {
          await submitFeedback(
            data.description,
            data.category || null,
          );
          return data;
        }}
        afterSubmitFunc={async () => {
          props.onSuccess();
        }}
        initialData={{ description: '', category: '' }}
        validationSchema={{
          description: {
            type: 'string',
            constraints: { required: true },
          },
        }}
        buttons={(
          <div className={styles.reportIssueActions}>
            <CancelButton
              text="Cancel"
              onClick={async () => { props.onCancel(); }}
            />
            <SubmitButton text="Submit" />
          </div>
        )}
        className={styles.reportIssueFormBody}
        testID="chat-report-issue-form-template"
      >
        <TextArea
          source="description"
          label="Description"
          hideLabel
          placeholder="Describe what went wrong..."
        />
        <FeedbackCategoryDropdown />
      </FormTemplate>
    </InChatFormTheme>
  );
}

export default function ReportIssueWidget() {
  const {
    currentConversationId,
    isStreaming,
    messages,
  } = useChatWidgetContext();

  const [state, setState] = useState<WidgetState>('idle');
  const handleDismiss = useCallback(() => setState('idle'), []);

  const hasAssistantMessages = messages.some(
    (m) => m.role === 'assistant' && !m.isError,
  );

  if (!currentConversationId || isStreaming || !hasAssistantMessages) {
    return null;
  } else if (state === 'idle') {
    return (
      <div className={styles.reportIssueContainer}>
        <button
          type="button"
          className={styles.reportIssueLink}
          onClick={() => setState('form')}
          data-testid="chat-report-issue-link"
        >
          Report an issue
        </button>
      </div>
    );
  } else if (state === 'success') {
    return (
      <div className={styles.reportIssueContainer}>
        <SuccessMessage onDismiss={handleDismiss} />
      </div>
    );
  } else {
    return (
      <div className={styles.reportIssueContainer}>
        <ReportIssueForm
          onSuccess={() => setState('success')}
          onCancel={() => setState('idle')}
        />
      </div>
    );
  }
}
